import type { ReactNode } from 'react';
import type { AudienceFieldDefinition, BrandFieldDefinition, BrandSourceTag } from '@mg/shared';
import { Input, Textarea } from '../../../components/ui/Field';
import { BrandFieldShell } from './BrandFieldShell';
import { brandFieldDescribedBy, textCounter, textLimitError } from './fieldParts';

/**
 * A brand text field (Text or Textarea by its definition's kind; spec:211-240) in its shell: label,
 * source tags, hint, the control, then its error or counter.
 * - A Textarea is full width, four rows, one per row (spec:255).
 * - The counter shows from 80% of the limit. Over the limit: "Shorten this to {max} characters or
 *   fewer." and the step or page must refuse to save (use textLimitError in its check). There is no
 *   `maxLength` attribute, so a paste is never cut silently.
 * - `error` (from the page, such as a server's sentence) wins over the length error.
 */
export function BrandTextField({
  id,
  def,
  value,
  sources,
  aiSuggested = false,
  onChange,
  readOnly = false,
  disabled = false,
  error,
  changes,
  footer,
}: {
  id: string;
  def: BrandFieldDefinition | AudienceFieldDefinition;
  value: string | null;
  sources: readonly BrandSourceTag[];
  aiSuggested?: boolean;
  onChange: (next: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  error?: string | null;
  /** Replaces the control (Release 4's Show changes). */
  changes?: ReactNode;
  /** Under the field (Release 4's suggestion box). */
  footer?: ReactNode;
}) {
  const shownError = error ?? textLimitError(value, def.maxLength);
  const counter = readOnly ? null : textCounter(value, def.maxLength);
  const describedBy = brandFieldDescribedBy(id, { hint: def.hint, sources, aiSuggested, error: shownError, counter });
  const control = {
    id,
    value: value ?? '',
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
    readOnly,
    disabled,
    'aria-describedby': describedBy,
    'aria-invalid': shownError ? true : undefined,
  };
  return (
    <BrandFieldShell
      id={id}
      label={def.label}
      hint={def.hint}
      sources={sources}
      aiSuggested={aiSuggested}
      error={shownError}
      counter={counter}
      readOnly={readOnly}
      changes={changes}
      footer={footer}
    >
      {def.kind === 'textarea' ? <Textarea rows={4} {...control} /> : <Input type="text" {...control} />}
    </BrandFieldShell>
  );
}
